export const CANCELLATION_POLICY_VALUES = [
  'no_refund',
  '1_week_before',
  '48_hours_before',
  '24_hours_before',
  'until_class_start',
] as const;

export type CancellationPolicy = (typeof CANCELLATION_POLICY_VALUES)[number];

export const DEFAULT_CANCELLATION_POLICY: CancellationPolicy = '48_hours_before';

export const CANCELLATION_POLICY_OPTIONS: Array<{
  value: CancellationPolicy;
  label: string;
  description: string;
}> = [
  {
    value: 'no_refund',
    label: 'No refund',
    description: 'Refunds are not automatically available once a booking is placed.',
  },
  {
    value: '1_week_before',
    label: '1 week before',
    description: 'Refunds are automatically approved until 7 days before class start.',
  },
  {
    value: '48_hours_before',
    label: '48 hours before',
    description: 'Refunds are automatically approved until 48 hours before class start.',
  },
  {
    value: '24_hours_before',
    label: '24 hours before',
    description: 'Refunds are automatically approved until 24 hours before class start.',
  },
  {
    value: 'until_class_start',
    label: 'Until class start',
    description: 'Refunds are automatically approved until the session begins.',
  },
];

export function isCancellationPolicy(value: unknown): value is CancellationPolicy {
  return (
    typeof value === 'string' && CANCELLATION_POLICY_VALUES.includes(value as CancellationPolicy)
  );
}

export function getCancellationPolicyLabel(policy: CancellationPolicy): string {
  return (
    CANCELLATION_POLICY_OPTIONS.find(option => option.value === policy)?.label ?? '48 hours before'
  );
}

export function getCancellationPolicyDescription(policy: CancellationPolicy): string {
  return (
    CANCELLATION_POLICY_OPTIONS.find(option => option.value === policy)?.description ??
    CANCELLATION_POLICY_OPTIONS.find(option => option.value === DEFAULT_CANCELLATION_POLICY)
      ?.description ??
    ''
  );
}

/**
 * Customer-facing refund statement for a class's cancellation policy: states the
 * cutoff time and that approved refunds are returned minus the non-refundable
 * credit card processing fee.
 */
export function getCancellationPolicyStatement(policy: CancellationPolicy): string {
  const feeNote = 'refunded minus the credit card processing fee';
  switch (policy) {
    case 'no_refund':
      return 'This class is non-refundable once booked.';
    case '1_week_before':
      return `Cancellation requests are approved up until 1 week before the class starts, ${feeNote}.`;
    case '48_hours_before':
      return `Cancellation requests are approved up until 48 hours before the class starts, ${feeNote}.`;
    case '24_hours_before':
      return `Cancellation requests are approved up until 24 hours before the class starts, ${feeNote}.`;
    case 'until_class_start':
      return `Cancellation requests are approved up until the class starts, ${feeNote}.`;
    default:
      return `Cancellation requests are approved up until 48 hours before the class starts, ${feeNote}.`;
  }
}

function getPolicyCutoffMs(policy: CancellationPolicy): number | null {
  switch (policy) {
    case '1_week_before':
      return 7 * 24 * 60 * 60 * 1000;
    case '48_hours_before':
      return 48 * 60 * 60 * 1000;
    case '24_hours_before':
      return 24 * 60 * 60 * 1000;
    case 'until_class_start':
      return 0;
    case 'no_refund':
    default:
      return null;
  }
}

export type RefundEligibility =
  | 'auto_approve'
  | 'creator_review'
  | 'deny_completed_session'
  | 'deny_policy';

// In-app cancellation/refund requests stay open until 1 day after the class
// starts; past this the UI directs the student to email the instructor instead.
// Must match CANCELLATION_REQUEST_GRACE_MS in client/src/lib/enrollment-dashboard.ts.
export const POST_SESSION_REQUEST_GRACE_MS = 24 * 60 * 60 * 1000;

export function getRefundEligibility(
  policy: CancellationPolicy,
  sessionStartDateTime: Date | string,
  now: Date = new Date()
): RefundEligibility {
  const startDate =
    sessionStartDateTime instanceof Date ? sessionStartDateTime : new Date(sessionStartDateTime);

  if (Number.isNaN(startDate.getTime())) {
    return 'deny_policy';
  }

  const startMs = startDate.getTime();
  const nowMs = now.getTime();

  // Past the grace period the class is "completed" — no in-app request.
  if (nowMs >= startMs + POST_SESSION_REQUEST_GRACE_MS) {
    return 'deny_completed_session';
  }
  // During the class and the day after, the instructor reviews (and may deny).
  if (nowMs >= startMs) {
    return 'creator_review';
  }

  const cutoffMs = getPolicyCutoffMs(policy);
  if (cutoffMs === null) {
    return 'creator_review';
  }

  return nowMs <= startMs - cutoffMs ? 'auto_approve' : 'creator_review';
}

// Who absorbs the non-refundable card processing fee when a refund is approved.
//   customer → partial refund (amount paid minus the fee)
//   creator / platform → full refund (that party eats the fee)
export type FeeBearer = 'customer' | 'creator' | 'platform';

export const FEE_BEARER_VALUES: readonly FeeBearer[] = ['customer', 'creator', 'platform'];

export function isFeeBearer(value: unknown): value is FeeBearer {
  return typeof value === 'string' && FEE_BEARER_VALUES.includes(value as FeeBearer);
}

/**
 * Whether a creator/admin may DENY a cancellation requested at `now`.
 * Within the policy window the request must be honored (full or partial);
 * past the window (or for no_refund) it's at the reviewer's discretion.
 */
export function cancellationDenyAllowed(
  policy: CancellationPolicy,
  sessionStartDateTime: Date | string,
  now: Date = new Date()
): boolean {
  return getRefundEligibility(policy, sessionStartDateTime, now) !== 'auto_approve';
}

// ---------------------------------------------------------------------------
// Delayed capture ("holding period")
//
// For bookings close to class start we place a card *authorization hold* and
// capture only when the cancellation window closes, so cancelling before then
// is free to everyone (a released hold incurs no Stripe fee). A card auth hold
// is only valid ~7 days (a card-network limit), so we can only defer when the
// cancellation cutoff falls within that window — i.e. near-term bookings.
//
// This module owns the *pure decision logic*; it has no Stripe/DB side effects
// and no runtime effect until callers wire it in behind DELAYED_CAPTURE_ENABLED.
// See docs/delayed-capture-implementation-plan.md.
// ---------------------------------------------------------------------------

export type CaptureMode = 'immediate' | 'deferred';

export interface CaptureStrategy {
  /** 'deferred' → authorize now, capture at `captureAt`. 'immediate' → charge now (today's behavior). */
  mode: CaptureMode;
  /** For deferred captures, when to capture (the cancellation cutoff). null for immediate. */
  captureAt: Date | null;
  /** Machine-readable rationale, for logs/telemetry. */
  reason:
    | 'within_hold_window'
    | 'feature_disabled'
    | 'non_card_method'
    | 'invalid_session_date'
    | 'no_refund_policy'
    | 'cutoff_passed'
    | 'beyond_hold_window';
}

/**
 * Maximum time we will hold a card authorization, with margin under the ~7-day
 * card-network limit so a slightly delayed capture sweep can never let the hold
 * expire.
 */
export const MAX_AUTH_HOLD_MS = 6 * 24 * 60 * 60 * 1000;

/**
 * Decide whether a booking should authorize-and-hold (deferred capture) or
 * charge immediately. Pure — no side effects.
 *
 * Defers only when: the feature is enabled, the method supports manual capture
 * (cards), the policy has a real cutoff (not `no_refund`), and that cutoff is in
 * the future but no further out than {@link MAX_AUTH_HOLD_MS}. Otherwise charges
 * immediately (the always-safe fallback).
 */
export function computeCaptureStrategy(params: {
  policy: CancellationPolicy;
  sessionStartDateTime: Date | string;
  /** Master feature flag (DELAYED_CAPTURE_ENABLED). */
  enabled: boolean;
  /** Whether the chosen payment method supports manual capture. Defaults true (card). */
  cardEligible?: boolean;
  now?: Date;
}): CaptureStrategy {
  const { policy, sessionStartDateTime, enabled, cardEligible = true, now = new Date() } = params;

  const immediate = (reason: CaptureStrategy['reason']): CaptureStrategy => ({
    mode: 'immediate',
    captureAt: null,
    reason,
  });

  if (!enabled) return immediate('feature_disabled');
  if (!cardEligible) return immediate('non_card_method');

  const startDate =
    sessionStartDateTime instanceof Date ? sessionStartDateTime : new Date(sessionStartDateTime);
  if (Number.isNaN(startDate.getTime())) return immediate('invalid_session_date');

  const cutoffMs = getPolicyCutoffMs(policy);
  if (cutoffMs === null) return immediate('no_refund_policy');

  // Capture at the cancellation cutoff — after it, a cancellation isn't
  // auto-refundable anyway, so that's when the sale becomes final.
  const captureAtMs = startDate.getTime() - cutoffMs;
  const nowMs = now.getTime();

  if (captureAtMs <= nowMs) return immediate('cutoff_passed');
  if (captureAtMs > nowMs + MAX_AUTH_HOLD_MS) return immediate('beyond_hold_window');

  return { mode: 'deferred', captureAt: new Date(captureAtMs), reason: 'within_hold_window' };
}
